<script setup>
import {computed, getCurrentInstance, h, nextTick, onMounted, onUnmounted, ref, render, watch} from 'vue';
import {_mft} from '../../../i18n';
import {aiPageBuilderBlocks, parseMfComponentConfig} from './component-blocks';
import {INTERACTIVE_CONTROL_SELECTOR} from './interactive-runtime';
import {attachResolvedMediaEntriesToContent, extendAppContextWithMediaEntries, resolveHtmlMediaUrls} from './media-lookup';

const props = defineProps({
    html: { type: String, default: '' },
    deviceMode: { type: String, default: 'desktop' },
    mediaUrlMap: { type: Object, default: () => ({}) },
    mediaEntryMap: { type: Object, default: () => ({}) },
    interactionLocked: { type: Boolean, default: false },
    containerType: { type: String, required: true },
    containerRef: { type: String, default: '' },
    viewmode: { type: String, default: 'default'}
});

const emit = defineEmits(['update-html', 'edit-component', 'edit-image']);

const iframeRef = ref(null);
const iframeRenderKey = ref(0);
const isIframeMounted = ref(true);
const isIframeRefreshing = ref(false);
const lastScrollY = ref(0);
const renderedHtml = ref('');
const inheritedHeadHtml = ref('');
const snapshotRoot = ref(null);
const isRenderedHtmlPending = ref(false);
const isTextEditorOpen = ref(false);
const isTextEditorContentReady = ref(false);
const selectedTextElement = ref(null);
const editorDraftHtml = ref('');
const editorContentRef = ref(null);
const currentInstance = getCurrentInstance();
let renderToken = 0;
let headObserver = null;
let scheduledRenderTimeout = null;
let textEditorFocusTimeout = null;

const previewTranslate = (key, args = {}) => _mft(key, args);

const buildBridgeScript = () => `
<script>
(function() {
    let selectedElement = null;
    const textSelector = 'p, h1, h2, h3, h4, h5, h6, span, li, td, th, label, a, button';
    const interactiveControlSelector = ${JSON.stringify(INTERACTIVE_CONTROL_SELECTOR)};

    function splitClassTokens(value) {
        return String(value || '').split(/\\s+/).map(function(token) {
            return token.trim();
        }).filter(Boolean);
    }

    function resolveTarget(root, trigger, attributeName) {
        var selector = trigger.getAttribute(attributeName || 'data-mf-target');
        if (!selector) return null;

        return root.querySelector(selector) || document.querySelector(selector);
    }

    function setHiddenState(element, shouldHide) {
        if (!element) return;

        element.classList.toggle('hidden', shouldHide);

        if (shouldHide) {
            element.setAttribute('hidden', '');
            return;
        }

        element.removeAttribute('hidden');
    }

    function isHidden(element) {
        return !element || element.hasAttribute('hidden') || element.classList.contains('hidden');
    }

    function syncTriggerState(trigger, isActive, ariaAttribute) {
        trigger.setAttribute(ariaAttribute || 'aria-expanded', isActive ? 'true' : 'false');

        var activeClasses = splitClassTokens(trigger.getAttribute('data-mf-active-class'));
        var inactiveClasses = splitClassTokens(trigger.getAttribute('data-mf-inactive-class'));

        activeClasses.forEach(function(className) {
            trigger.classList.toggle(className, isActive);
        });

        inactiveClasses.forEach(function(className) {
            trigger.classList.toggle(className, !isActive);
        });
    }

    function initializeInteractiveState() {
        document.querySelectorAll('[data-mf-toggle-target]').forEach(function(trigger) {
            var target = resolveTarget(document, trigger, 'data-mf-toggle-target');
            syncTriggerState(trigger, target ? !isHidden(target) : false, 'aria-pressed');
        });

        document.querySelectorAll('[data-mf-accordion]').forEach(function(accordion) {
            accordion.querySelectorAll('[data-mf-accordion-trigger]').forEach(function(trigger) {
                var target = resolveTarget(accordion, trigger, 'data-mf-target');
                syncTriggerState(trigger, target ? !isHidden(target) : false, 'aria-expanded');
            });
        });

        document.querySelectorAll('[data-mf-switcher]').forEach(function(switcher) {
            var triggers = Array.from(switcher.querySelectorAll('[data-mf-switcher-trigger]'));
            if (triggers.length === 0) return;

            var activeTrigger = triggers.find(function(trigger) {
                return trigger.getAttribute('data-mf-active') === 'true';
            }) || triggers.find(function(trigger) {
                var target = resolveTarget(switcher, trigger, 'data-mf-target');
                return target && !isHidden(target);
            }) || triggers[0];

            triggers.forEach(function(trigger) {
                var target = resolveTarget(switcher, trigger, 'data-mf-target');
                var isActive = trigger === activeTrigger;
                syncTriggerState(trigger, isActive, 'aria-pressed');
                setHiddenState(target, !isActive);
            });
        });
    }

    function handleInteractiveClick(trigger, event) {
        if (trigger.matches('[data-mf-toggle-target]')) {
            var toggleTarget = resolveTarget(document, trigger, 'data-mf-toggle-target');
            if (!toggleTarget) return;

            event.preventDefault();
            event.stopPropagation();

            var shouldShow = isHidden(toggleTarget);
            setHiddenState(toggleTarget, !shouldShow);
            syncTriggerState(trigger, shouldShow, 'aria-pressed');
            return;
        }

        if (trigger.matches('[data-mf-accordion-trigger]')) {
            var accordion = trigger.closest('[data-mf-accordion]');
            if (!accordion) return;

            var accordionTarget = resolveTarget(accordion, trigger, 'data-mf-target');
            if (!accordionTarget) return;

            event.preventDefault();
            event.stopPropagation();

            var shouldOpenTarget = isHidden(accordionTarget);

            if (!shouldOpenTarget) {
                syncTriggerState(trigger, true, 'aria-expanded');
                return;
            }

            accordion.querySelectorAll('[data-mf-accordion-trigger]').forEach(function(candidate) {
                var candidateTarget = resolveTarget(accordion, candidate, 'data-mf-target');
                var isActive = shouldOpenTarget && candidate === trigger;
                setHiddenState(candidateTarget, !isActive);
                syncTriggerState(candidate, isActive, 'aria-expanded');
            });
            return;
        }

        if (trigger.matches('[data-mf-switcher-trigger]')) {
            var switcher = trigger.closest('[data-mf-switcher]');
            if (!switcher) return;

            event.preventDefault();
            event.stopPropagation();

            switcher.querySelectorAll('[data-mf-switcher-trigger]').forEach(function(candidate) {
                var candidateTarget = resolveTarget(switcher, candidate, 'data-mf-target');
                var isActive = candidate === trigger;
                syncTriggerState(candidate, isActive, 'aria-pressed');
                setHiddenState(candidateTarget, !isActive);
            });
        }
    }

    function clearSelectedElement() {
        if (!selectedElement) return;
        selectedElement.style.outline = '';
        selectedElement.style.outlineOffset = '';
        selectedElement = null;
    }

    function setSelectedElement(el) {
        if (selectedElement === el) return;
        clearSelectedElement();
        selectedElement = el;
        selectedElement.style.outline = '2px solid #3b82f6';
        selectedElement.style.outlineOffset = '-2px';
    }

    function selectTextElement(textElement) {
        if (!textElement || textElement.closest('mf-component')) return false;

        var path = getElementPath(textElement);
        if (!path) return false;

        setSelectedElement(textElement);
        window.parent.postMessage({
            type: 'element-selected',
            tag: textElement.tagName,
            html: textElement.innerHTML || '',
            path: path
        }, '*');

        return true;
    }

    function findEditableAccordionTextElement(trigger, clickedElement) {
        if (!trigger) return null;

        var clickedTextElement = clickedElement && clickedElement.closest
            ? clickedElement.closest(textSelector)
            : null;

        if (clickedTextElement && trigger.contains(clickedTextElement) && clickedTextElement !== trigger) {
            return clickedTextElement;
        }

        var descendantTextElements = Array.from(trigger.querySelectorAll(textSelector));
        for (var i = 0; i < descendantTextElements.length; i++) {
            var candidate = descendantTextElements[i];

            if (candidate !== trigger && candidate.textContent && candidate.textContent.trim()) {
                return candidate;
            }
        }

        if (trigger.matches(textSelector)) {
            return trigger;
        }

        return null;
    }

    function findOpenAccordionContentElement(clickedElement) {
        if (!clickedElement || !clickedElement.closest) return null;

        var accordion = clickedElement.closest('[data-mf-accordion]');
        if (!accordion) return null;

        var openAccordionContentElement = null;

        accordion.querySelectorAll('[data-mf-accordion-trigger]').forEach(function(trigger) {
            if (openAccordionContentElement) return;

            var target = resolveTarget(accordion, trigger, 'data-mf-target');
            if (!target || isHidden(target) || !target.contains(clickedElement)) return;

            var clickedTextElement = clickedElement.closest(textSelector);
            if (clickedTextElement && target.contains(clickedTextElement)) {
                openAccordionContentElement = clickedTextElement;
                return;
            }

            var descendantTextElements = Array.from(target.querySelectorAll(textSelector));
            for (var i = 0; i < descendantTextElements.length; i++) {
                var candidate = descendantTextElements[i];

                if (candidate.textContent && candidate.textContent.trim()) {
                    openAccordionContentElement = candidate;
                    return;
                }
            }

            if (target.textContent && target.textContent.trim()) {
                openAccordionContentElement = target;
            }
        });

        return openAccordionContentElement;
    }

    function getElementPath(el) {
        var container = document.getElementById('ai-page-content');
        var path = [];
        var current = el;

        while (current && current !== container) {
            var parent = current.parentElement;
            if (!parent) return null;
            path.unshift(Array.prototype.indexOf.call(parent.children, current));
            current = parent;
        }

        return current === container ? path : null;
    }

    function getElementByPath(path) {
        var container = document.getElementById('ai-page-content');
        if (!container || !Array.isArray(path)) return null;

        var current = container;
        for (var i = 0; i < path.length; i++) {
            current = current.children[path[i]];
            if (!current) return null;
        }

        return current;
    }

    document.addEventListener('mouseover', function(e) {
        var el = e.target;
        if (el === document.body || el === document.documentElement) return;
        if (el.classList && el.classList.contains('mf-drag-handle')) return;
        if (el.closest && el.closest(interactiveControlSelector)) return;
        if (el.closest && el.closest('mf-component')) return;
        if (selectedElement === el) return;
        el.style.outline = '2px solid rgba(59, 130, 246, 0.5)';
        el.style.outlineOffset = '-2px';
    });

    document.addEventListener('mouseout', function(e) {
        var el = e.target;
        if (selectedElement === el) return;
        if (el.closest && el.closest('mf-component')) return;
        el.style.outline = '';
        el.style.outlineOffset = '';
    });

    document.addEventListener('click', function(e) {
        const el = e.target;
        if (el.tagName === 'A') {
            e.preventDefault();
        }
        if (el.classList && el.classList.contains('mf-drag-handle')) return;
        if (el.closest) {
            var editableAccordionContentElement = findOpenAccordionContentElement(el);

            if (selectTextElement(editableAccordionContentElement)) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }

            var accordionTrigger = el.closest('[data-mf-accordion-trigger]');

            if (accordionTrigger) {
                var accordion = accordionTrigger.closest('[data-mf-accordion]');
                var accordionTarget = accordion ? resolveTarget(accordion, accordionTrigger, 'data-mf-target') : null;

                if (accordionTarget && !isHidden(accordionTarget)) {
                    var editableAccordionTextElement = findEditableAccordionTextElement(accordionTrigger, el);

                    if (selectTextElement(editableAccordionTextElement)) {
                        e.preventDefault();
                        e.stopPropagation();
                    }

                    return;
                }
            }

            if (el.closest(interactiveControlSelector)) return;
        }

        var comp = el.closest('mf-component');
        if (comp) {
            var allComps = Array.from(document.querySelectorAll('mf-component'));
            var index = allComps.indexOf(comp);
            var config = {};
            try { config = JSON.parse(comp.getAttribute('config') || '{}'); } catch(ex) {}
            window.parent.postMessage({
                type: 'component-clicked',
                componentType: comp.getAttribute('type') || '',
                config: config,
                index: index
            }, '*');
            return;
        }

        var img = (el.tagName === 'IMG') ? el : el.closest('img');
        if (img) {
            var allImages = Array.from(document.querySelectorAll('img'));
            window.parent.postMessage({
                type: 'image-clicked',
                index: allImages.indexOf(img),
                currentSrc: img.getAttribute('src') || '',
                currentAlt: img.getAttribute('alt') || '',
                mediaId: img.getAttribute('data-media-id') || null,
            }, '*');
            return;
        }

        var textElement = el.closest ? el.closest(textSelector) : null;
        selectTextElement(textElement);
    });

    document.addEventListener('click', function(e) {
        var trigger = e.target.closest(interactiveControlSelector);
        if (!trigger) return;
        handleInteractiveClick(trigger, e);
    });

    function getCleanHtml() {
        var container = document.getElementById('ai-page-content');
        var clone = container.cloneNode(true);
        var uiEls = clone.querySelectorAll('.mf-drag-handle, [data-mf-preview-render-root], [data-mf-preview-badge], [data-mf-preview-overlay]');
        for (var i = 0; i < uiEls.length; i++) uiEls[i].remove();
        return clone.innerHTML;
    }

    var draggedEl = null;

    function initDragDrop() {
        var container = document.getElementById('ai-page-content');
        if (!container) return;
        var children = container.children;
        for (var i = 0; i < children.length; i++) {
            var child = children[i];
            if (child.id === 'nav-preview-bar') continue;
            child.setAttribute('draggable', 'true');
            if (!child.querySelector('.mf-drag-handle')) {
                var handle = document.createElement('div');
                handle.className = 'mf-drag-handle';
                handle.textContent = '\\u2195';
                handle.setAttribute('title', ${JSON.stringify(previewTranslate('page:aiPageBuilder.preview.dragToReorder'))});
                child.appendChild(handle);
            }
        }

        container.addEventListener('dragstart', function(e) {
            var section = e.target.closest('#ai-page-content > *');
            if (!section || section.id === 'nav-preview-bar') return;
            draggedEl = section;
            section.classList.add('mf-dragging');
            e.dataTransfer.effectAllowed = 'move';
        });

        container.addEventListener('dragover', function(e) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (!draggedEl) return;
            var target = e.target.closest('#ai-page-content > *');
            if (!target || target === draggedEl || target.id === 'nav-preview-bar') return;
            clearIndicators();
            var rect = target.getBoundingClientRect();
            var midY = rect.top + rect.height / 2;
            if (e.clientY < midY) {
                target.classList.add('mf-drag-over-top');
            } else {
                target.classList.add('mf-drag-over-bottom');
            }
        });

        container.addEventListener('dragleave', function(e) {
            var target = e.target.closest('#ai-page-content > *');
            if (target) {
                target.classList.remove('mf-drag-over-top', 'mf-drag-over-bottom');
            }
        });

        container.addEventListener('drop', function(e) {
            e.preventDefault();
            if (!draggedEl) return;
            var target = e.target.closest('#ai-page-content > *');
            if (!target || target === draggedEl || target.id === 'nav-preview-bar' || target.classList.contains('mf-insert-point')) {
                cleanupDrag();
                return;
            }
            var rect = target.getBoundingClientRect();
            var midY = rect.top + rect.height / 2;
            if (e.clientY < midY) {
                container.insertBefore(draggedEl, target);
            } else {
                container.insertBefore(draggedEl, target.nextSibling);
            }
            cleanupDrag();
            window.parent.postMessage({ type: 'element-edited', html: getCleanHtml() }, '*');
        });

        container.addEventListener('dragend', function() {
            cleanupDrag();
        });
    }

    function clearIndicators() {
        var items = document.querySelectorAll('.mf-drag-over-top, .mf-drag-over-bottom');
        for (var i = 0; i < items.length; i++) {
            items[i].classList.remove('mf-drag-over-top', 'mf-drag-over-bottom');
        }
    }

    function cleanupDrag() {
        clearIndicators();
        if (draggedEl) {
            draggedEl.classList.remove('mf-dragging');
            draggedEl = null;
        }
    }

    initializeInteractiveState();
    initDragDrop();

    window.addEventListener('scroll', function() {
        window.parent.postMessage({ type: 'scroll-position', y: window.scrollY }, '*');
    });

    function restoreScrollPosition(y) {
        var root = document.documentElement;
        var body = document.body;
        var previousRootScrollBehavior = root ? root.style.scrollBehavior : '';
        var previousBodyScrollBehavior = body ? body.style.scrollBehavior : '';

        if (root) {
            root.style.scrollBehavior = 'auto';
        }

        if (body) {
            body.style.scrollBehavior = 'auto';
        }

        window.scrollTo({ top: y, left: 0, behavior: 'auto' });

        window.requestAnimationFrame(function() {
            if (root) {
                root.style.scrollBehavior = previousRootScrollBehavior;
            }

            if (body) {
                body.style.scrollBehavior = previousBodyScrollBehavior;
            }
        });
    }

    window.addEventListener('message', function(e) {
        if (!e.data) return;

        if (e.data.type === 'restore-scroll') {
            restoreScrollPosition(e.data.y);
            return;
        }

        if (e.data.type === 'set-active-element') {
            var nextSelectedElement = getElementByPath(e.data.path);
            if (nextSelectedElement) {
                setSelectedElement(nextSelectedElement);
            }
            return;
        }

        if (e.data.type === 'clear-active-element') {
            clearSelectedElement();
        }
    });
})();
<\/script>
`;

const BLOCK_LEVEL_TAGS = new Set([
    'address',
    'article',
    'aside',
    'blockquote',
    'div',
    'dl',
    'dt',
    'dd',
    'fieldset',
    'figcaption',
    'figure',
    'footer',
    'header',
    'hr',
    'li',
    'main',
    'nav',
    'ol',
    'p',
    'pre',
    'section',
    'table',
    'tbody',
    'td',
    'tfoot',
    'th',
    'thead',
    'tr',
    'ul',
]);
const ALLOWED_INLINE_TAGS = new Set(['a', 'abbr', 'b', 'br', 'code', 'em', 'i', 'mark', 's', 'small', 'span', 'strong', 'sub', 'sup', 'u']);
const SHARED_ALLOWED_ATTRIBUTES = new Set(['class', 'style', 'title']);
const LINK_ALLOWED_ATTRIBUTES = new Set(['href', 'target', 'rel']);

const isSafeUrl = (value = '') => {
    const normalized = String(value).trim();

    if (!normalized) {
        return true;
    }

    const hasScheme = /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(normalized);

    if (!hasScheme) {
        return true;
    }

    return /^(https?:|mailto:|tel:)/i.test(normalized);
};

const normalizeEditableHtml = (html = '', hostTag = 'DIV') => {
    if (typeof document === 'undefined') {
        return html;
    }

    const template = document.createElement('template');
    template.innerHTML = html;
    const hostTagName = String(hostTag || 'DIV').toLowerCase();

    const sanitizeNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
            node.remove();
            return;
        }

        const tagName = node.tagName.toLowerCase();

        Array.from(node.childNodes).forEach(sanitizeNode);

        if (['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math', 'form', 'input', 'textarea', 'select'].includes(tagName)) {
            node.remove();
            return;
        }

        if (BLOCK_LEVEL_TAGS.has(tagName)) {
            const fragment = document.createDocumentFragment();
            const children = Array.from(node.childNodes);

            children.forEach((child, index) => {
                fragment.appendChild(child);

                if (index < children.length - 1) {
                    fragment.appendChild(document.createElement('br'));
                }
            });

            node.replaceWith(fragment);
            return;
        }

        if (!ALLOWED_INLINE_TAGS.has(tagName) || tagName === hostTagName) {
            node.replaceWith(...Array.from(node.childNodes));
            return;
        }

        Array.from(node.attributes).forEach((attribute) => {
            const attributeName = attribute.name.toLowerCase();
            const isSharedAttribute = SHARED_ALLOWED_ATTRIBUTES.has(attributeName);
            const isLinkAttribute = tagName === 'a' && LINK_ALLOWED_ATTRIBUTES.has(attributeName);

            if (attributeName.startsWith('on') || (!isSharedAttribute && !isLinkAttribute)) {
                node.removeAttribute(attribute.name);
            }
        });

        if (tagName === 'a') {
            if (!isSafeUrl(node.getAttribute('href'))) {
                node.removeAttribute('href');
            }

            if (node.getAttribute('target') === '_blank') {
                node.setAttribute('rel', 'noopener noreferrer');
            }
        }
    };

    Array.from(template.content.childNodes).forEach(sanitizeNode);

    const wrapper = document.createElement('div');
    wrapper.appendChild(template.content.cloneNode(true));

    let normalizedHtml = wrapper.innerHTML;
    normalizedHtml = normalizedHtml.replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>');
    normalizedHtml = normalizedHtml.replace(/^(?:\s|<br\s*\/?>)+/i, '');
    normalizedHtml = normalizedHtml.replace(/(?:\s|<br\s*\/?>)+$/i, '');

    return normalizedHtml;
};

const findNodeByPath = (root, path = []) => {
    let current = root;

    for (const index of path) {
        if (!current?.children?.[index]) {
            return null;
        }

        current = current.children[index];
    }

    return current;
};

const syncEditorContent = () => {
    if (!editorContentRef.value || editorContentRef.value.innerHTML === editorDraftHtml.value) {
        return;
    }

    editorContentRef.value.innerHTML = editorDraftHtml.value;
};

const focusEditor = async () => {
    await nextTick();
    syncEditorContent();

    if (!editorContentRef.value) {
        return;
    }

    editorContentRef.value.focus();

    const selection = window.getSelection();
    if (!selection) {
        return;
    }

    const range = document.createRange();
    range.selectNodeContents(editorContentRef.value);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
};

const postMessageToIframe = (payload) => {
    if (!iframeRef.value?.contentWindow) {
        return;
    }

    iframeRef.value.contentWindow.postMessage(payload, '*');
};

const getSelectedElementPath = () => {
    const path = selectedTextElement.value?.path;

    return Array.isArray(path) ? [...path] : null;
};

const syncSelectedElementInIframe = () => {
    const selectedPath = getSelectedElementPath();

    if (isTextEditorOpen.value && selectedPath?.length) {
        postMessageToIframe({
            type: 'set-active-element',
            path: selectedPath,
        });
        return;
    }

    postMessageToIframe({ type: 'clear-active-element' });
};

const refreshIframe = async () => {
    if (isIframeRefreshing.value) {
        return;
    }

    isIframeRefreshing.value = true;
    isIframeMounted.value = false;
    await nextTick();
    iframeRenderKey.value += 1;
    isIframeMounted.value = true;
    await nextTick();
    isIframeRefreshing.value = false;
};

const openTextEditor = async ({ html = '', path = [], tag = 'DIV' }) => {
    selectedTextElement.value = {
        path: Array.isArray(path) ? [...path] : [],
        tag,
    };
    editorDraftHtml.value = normalizeEditableHtml(html, tag);
    isTextEditorContentReady.value = false;
    isTextEditorOpen.value = true;
    syncSelectedElementInIframe();

    window.clearTimeout(textEditorFocusTimeout);
    textEditorFocusTimeout = window.setTimeout(() => {
        isTextEditorContentReady.value = true;
        void focusEditor();
    }, 60);
};

const closeTextEditor = () => {
    window.clearTimeout(textEditorFocusTimeout);
    textEditorFocusTimeout = null;
    isTextEditorOpen.value = false;
    isTextEditorContentReady.value = false;
    selectedTextElement.value = null;
    editorDraftHtml.value = '';
    syncSelectedElementInIframe();
};

const applyEditedHtmlToSource = (sourceHtml, path, nextInnerHtml) => {
    if (typeof document === 'undefined') {
        return sourceHtml;
    }

    const container = document.createElement('div');
    container.innerHTML = sourceHtml || '';
    const target = findNodeByPath(container, path);

    if (!target) {
        return sourceHtml;
    }

    target.innerHTML = normalizeEditableHtml(nextInnerHtml, target.tagName);

    return container.innerHTML;
};

const saveTextEdit = () => {
    if (!selectedTextElement.value) {
        closeTextEditor();
        return;
    }

    const nextHtml = applyEditedHtmlToSource(props.html, selectedTextElement.value.path, editorDraftHtml.value);
    emit('update-html', nextHtml);
    closeTextEditor();
};

const handleEditorInput = (event) => {
    editorDraftHtml.value = event.target.innerHTML;
};

const handleEditorKeydown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        saveTextEdit();
    }
};

const applyEditorFormat = async (command) => {
    await nextTick();

    if (!editorContentRef.value) {
        return;
    }

    editorContentRef.value.focus();
    document.execCommand(command, false);
    editorDraftHtml.value = editorContentRef.value.innerHTML;
};

const resolveMediaUrls = (html) => resolveHtmlMediaUrls(html, props.mediaEntryMap, props.mediaUrlMap);
const hasPreviewComponents = (html = '') => /<mf-component(?:\s|>)/i.test(html);

const escapeHtmlAttribute = (value = '') => {
    return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('"', '&quot;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;');
};

const previewBlockLocales = Object.entries(import.meta.glob('../../../locales/*/blocks.json', { eager: true }))
    .reduce((ack, [path, module]) => {
        const match = path.match(/\/locales\/([^/]+)\/blocks\.json$/);
        const locale = match?.[1];

        if (!locale) {
            return ack;
        }

        ack[locale] = module.default || module;

        return ack;
    }, {});

const previewBlockTranslationKeyAliases = {
    'button-unified': 'single-button',
    'coaching-bookings': 'coachingBookings',
    'course-feed': 'courseFeed',
    'external-widget': 'externalWidget',
    'image-unified': 'image',
    'licensed-courses': 'licensedCourses',
    'login-form': 'loginForm',
    'pdf-viewer': 'pdfViewer',
    'single-course': 'singleCourse',
    'slideshow-advanced': 'slideshow',
    'task-lists': 'taskLists',
    'wl-app-spacer': 'spacer',
};

const toCamelCase = (value = '') => String(value).replace(/-([a-z])/g, (_, char) => char.toUpperCase());

const resolvePreviewBlockTranslationKey = (type = '') => {
    const normalizedType = String(type).trim();

    if (!normalizedType) {
        return '';
    }

    return previewBlockTranslationKeyAliases[normalizedType] || normalizedType;
};

const translatePreviewBlockType = (type = '') => {
    const resolvedType = resolvePreviewBlockTranslationKey(type);

    if (!resolvedType) {
        return '';
    }

    const locale = typeof document === 'undefined'
        ? 'en'
        : document.documentElement.lang.slice(0, 2) || 'en';
    const translationKeys = [...new Set([resolvedType, toCamelCase(resolvedType)])].filter(Boolean);
    const localizedBlocks = previewBlockLocales[locale] || {};
    const fallbackBlocks = previewBlockLocales.en || {};

    for (const translationKey of translationKeys) {
        const translatedType = localizedBlocks[translationKey] || fallbackBlocks[translationKey];

        if (!translatedType) {
            continue;
        }

        return translatedType;
    }

    return String(type).trim();
};

const buildPreviewComponentLabel = (type = '') => {
    const translatedType = translatePreviewBlockType(type);

    if (!translatedType) {
        return previewTranslate('page:aiPageBuilder.preview.componentLabelDefault');
    }

    return translatedType;
};

const syncInheritedHeadHtml = () => {
    if (typeof document === 'undefined') {
        inheritedHeadHtml.value = '';
        return;
    }

    const fragments = [`<base href="${escapeHtmlAttribute(document.baseURI)}">`];
    const seenStylesheets = new Set();

    for (const link of document.head.querySelectorAll('link[rel="stylesheet"][href]')) {
        const href = link.href;

        if (!href || seenStylesheets.has(href)) {
            continue;
        }

        seenStylesheets.add(href);

        const media = link.media && link.media !== 'all'
            ? ` media="${escapeHtmlAttribute(link.media)}"`
            : '';

        fragments.push(`<link rel="stylesheet" href="${escapeHtmlAttribute(href)}"${media}>`);
    }

    for (const style of document.head.querySelectorAll('style')) {
        if (!style.textContent?.trim()) {
            continue;
        }

        fragments.push(`<style>${style.textContent}</style>`);
    }

    inheritedHeadHtml.value = fragments.join('\n');
};

const ensureSnapshotRoot = () => {
    if (snapshotRoot.value || typeof document === 'undefined') {
        return;
    }

    const root = document.createElement('div');
    root.setAttribute('aria-hidden', 'true');
    root.style.cssText = 'position: fixed; left: -99999px; top: 0; width: 1280px; opacity: 0; pointer-events: none; z-index: -1;';
    document.body.appendChild(root);
    snapshotRoot.value = root;
};

const buildFallbackHtml = (type) => {
    return `<div class="rounded-xl border-2 border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">${previewTranslate('page:aiPageBuilder.preview.fallbackComponent', {
        type: type || previewTranslate('page:aiPageBuilder.preview.unknownComponentType'),
    })}</div>`;
};

const previewUnavailableComponentTypes = new Set(['calendly', 'form', 'invoice-link', 'login-form']);

const buildPreviewUnavailableHtml = () => {
    return `<div class="flex min-h-[8rem] items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/70 px-6 py-8 text-center text-sm font-medium text-gray-500">${previewTranslate('page:aiPageBuilder.preview.unavailable')}</div>`;
};

const buildPreviewStatusHtml = (message) => {
    return `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    ${inheritedHeadHtml.value}
    <script src="https://cdn.tailwindcss.com"><\/script>
    <style>body { margin: 0; }</style>
</head>
<body class="custom-fonts-enabled ai-page-builder-page">
    <div id="ai-page-content" class="min-h-screen flex items-center justify-center text-gray-400">
        <div class="text-center">
            <p class="text-lg">${message}</p>
        </div>
    </div>
</body>
</html>`;
};

const renderComponentSnapshot = async (type, config) => {
    if (previewUnavailableComponentTypes.has(type)) {
        return buildPreviewUnavailableHtml();
    }

    const component = aiPageBuilderBlocks[type];

    if (!component) {
        return buildFallbackHtml(type);
    }

    ensureSnapshotRoot();

    if (!snapshotRoot.value) {
        return buildFallbackHtml(type);
    }

    const target = document.createElement('div');
    snapshotRoot.value.appendChild(target);

    try {
        const vnode = h(component, {
            content: attachResolvedMediaEntriesToContent(config, props.mediaEntryMap),
            containerRef: props.containerRef,
            uuid: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2),
        });

        if (currentInstance?.appContext) {
            vnode.appContext = extendAppContextWithMediaEntries(currentInstance.appContext, props.mediaEntryMap);
        }

        render(vnode, target);
        await nextTick();

        return target.innerHTML || buildFallbackHtml(type);
    } catch (e) {
        console.warn(`Failed to render preview component: ${type}`, e);
        return buildFallbackHtml(type);
    } finally {
        render(null, target);
        target.remove();
    }
};

const runRenderedHtmlUpdate = async () => {
    const token = ++renderToken;
    const resolvedHtml = resolveMediaUrls(props.html);
    isRenderedHtmlPending.value = hasPreviewComponents(resolvedHtml);

    if (!resolvedHtml) {
        renderedHtml.value = '';
        isRenderedHtmlPending.value = false;
        return;
    }

    const container = document.createElement('div');
    container.innerHTML = resolvedHtml;

    for (const element of container.querySelectorAll('mf-component')) {
        const type = element.getAttribute('type') || '';
        const config = parseMfComponentConfig(element);
        const snapshotHtml = await renderComponentSnapshot(type, config);

        if (token !== renderToken) {
            return;
        }

        element.innerHTML = `
            <div data-mf-preview-render-root>${snapshotHtml}</div>
            <button type="button" data-mf-preview-overlay aria-label="${escapeHtmlAttribute(previewTranslate('page:aiPageBuilder.preview.editComponent', { component: buildPreviewComponentLabel(type) }))}">
                <span data-mf-preview-overlay-label>${escapeHtmlAttribute(buildPreviewComponentLabel(type))}</span>
            </button>
        `;
    }

    renderedHtml.value = container.innerHTML;

    if (token === renderToken) {
        isRenderedHtmlPending.value = false;
    }
};

const scheduleRenderedHtmlUpdate = () => {
    if (typeof window === 'undefined') {
        return;
    }

    window.clearTimeout(scheduledRenderTimeout);
    scheduledRenderTimeout = window.setTimeout(() => {
        scheduledRenderTimeout = null;
        void runRenderedHtmlUpdate();
    }, 250);
};

const wrappedHtml = computed(() => {
    const resolvedHtml = resolveMediaUrls(props.html);

    if (isRenderedHtmlPending.value && hasPreviewComponents(resolvedHtml) && !renderedHtml.value) {
        return buildPreviewStatusHtml(previewTranslate('page:aiPageBuilder.preview.rendering'));
    }

    const currentHtml = renderedHtml.value || resolvedHtml;

    if (!currentHtml) {
        return buildPreviewStatusHtml(previewTranslate('page:aiPageBuilder.emptyState'));
    }

    const navPreview = props.containerType === 'website'
        ? `<div id="nav-preview-bar" class="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-3">
    <div class="w-8 h-8 bg-gray-200 rounded"></div>
    <div class="flex gap-4">
        <div class="h-2.5 w-16 bg-gray-200 rounded"></div>
        <div class="h-2.5 w-16 bg-gray-200 rounded"></div>
        <div class="h-2.5 w-16 bg-gray-200 rounded"></div>
    </div>
    <div class="ml-auto text-xs text-gray-400">${previewTranslate('page:aiPageBuilder.preview.navigationBar')}</div>
</div>`
        : '';

    return `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    ${inheritedHeadHtml.value}
    <script src="https://cdn.tailwindcss.com"><\/script>
    <style>
        body { margin: 0; }
        [contenteditable="true"] { cursor: text; }
        *:hover { transition: outline 0.1s ease; }
        img:hover { outline: 2px solid rgba(59, 130, 246, 0.5); cursor: pointer; }
        mf-component {
            display: block;
            position: relative;
            cursor: pointer;
            min-height: 8rem;
        }
        mf-component:hover {
            outline: 2px solid rgba(59, 130, 246, 0.5);
            outline-offset: 2px;
            border-radius: 0.5rem;
        }
        [data-mf-preview-render-root] {
            display: block;
            min-height: inherit;
        }
        [data-mf-preview-overlay] {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: flex-start;
            justify-content: flex-end;
            padding: 0.75rem;
            width: 100%;
            height: 100%;
            border: 0;
            border-radius: 0.75rem;
            background: rgba(59, 130, 246, 0);
            cursor: pointer;
            z-index: 6;
            opacity: 1;
            transition: background 0.2s ease;
        }
        [data-mf-preview-overlay-label] {
            padding: 0.4rem 0.85rem;
            border-radius: 9999px;
            border: 1px solid rgba(96, 165, 250, 0.35);
            background: rgba(219, 234, 254, 0.82);
            color: rgba(30, 64, 175, 0.62);
            font-size: 0.7rem;
            font-weight: 700;
            letter-spacing: 0.08em;
            line-height: 1.1;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            box-shadow: 0 10px 30px rgba(37, 99, 235, 0.08);
            transform: translateY(0);
            transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
        }
        mf-component:hover > [data-mf-preview-overlay],
        mf-component > [data-mf-preview-overlay]:focus-visible {
            background: rgba(59, 130, 246, 0.18);
        }
        mf-component:hover > [data-mf-preview-overlay] [data-mf-preview-overlay-label],
        mf-component > [data-mf-preview-overlay]:focus-visible [data-mf-preview-overlay-label] {
            border-color: rgba(37, 99, 235, 0.9);
            background: rgba(37, 99, 235, 0.92);
            color: #fff;
            box-shadow: 0 10px 30px rgba(37, 99, 235, 0.25);
            transform: translateY(-1px);
        }
        #ai-page-content > * { position: relative; }
        #ai-page-content > *:hover > .mf-drag-handle { opacity: 1; }
        .mf-drag-handle {
            position: absolute; top: 8px; left: 8px; opacity: 0;
            transition: opacity 0.2s; cursor: grab; z-index: 10;
            width: 20px; height: 20px; display: flex; align-items: center;
            justify-content: center; background: rgba(255,255,255,0.9);
            border: 1px solid #d1d5db; border-radius: 4px; font-size: 12px;
            color: #6b7280; line-height: 1;
        }
        .mf-drag-handle:hover { background: #eff6ff; border-color: #3b82f6; color: #3b82f6; }
        .mf-drag-over-top { border-top: 3px solid #3b82f6 !important; }
        .mf-drag-over-bottom { border-bottom: 3px solid #3b82f6 !important; }
        .mf-dragging { opacity: 0.4; }
    </style>
</head>
<body class="custom-fonts-enabled ai-page-builder-page">
    ${navPreview}
    <div id="ai-page-content">${currentHtml}</div>
    ${buildBridgeScript()}
</body>
</html>`;
});

const handleMessage = (event) => {
    if (!event.data || typeof event.data !== 'object') return;

    if (event.data.type === 'scroll-position') {
        lastScrollY.value = event.data.y;
        return;
    }

    if (props.interactionLocked) {
        return;
    }

    if (event.data.type === 'element-edited') {
        emit('update-html', event.data.html);
    }

    if (event.data.type === 'element-selected') {
        void openTextEditor({
            html: event.data.html,
            path: event.data.path,
            tag: event.data.tag,
        });
    }

    if (event.data.type === 'component-clicked') {
        emit('edit-component', {
            componentType: event.data.componentType,
            config: event.data.config,
            index: event.data.index,
        });
    }

    if (event.data.type === 'image-clicked') {
        emit('edit-image', {
            index: event.data.index,
            currentSrc: event.data.currentSrc,
            currentAlt: event.data.currentAlt,
            mediaId: event.data.mediaId,
        });
    }

};

const restoreScroll = () => {
    if (iframeRef.value && iframeRef.value.contentWindow) {
        setTimeout(() => {
            if (iframeRef.value && iframeRef.value.contentWindow) {
                iframeRef.value.contentWindow.postMessage({ type: 'restore-scroll', y: lastScrollY.value }, '*');
                syncSelectedElementInIframe();
            }
        }, 100);
    }
};

watch(
    [() => props.html, () => props.mediaUrlMap, () => props.mediaEntryMap, () => props.containerRef],
    () => {
        scheduleRenderedHtmlUpdate();
    },
    { deep: true, immediate: true }
);

watch(
    renderedHtml,
    (nextRenderedHtml, previousRenderedHtml) => {
        if (!nextRenderedHtml || nextRenderedHtml === previousRenderedHtml) {
            return;
        }

        if (!hasPreviewComponents(resolveMediaUrls(props.html)) || isIframeRefreshing.value) {
            return;
        }

        void refreshIframe();
    }
);

watch(
    () => props.html,
    () => {
        if (!isTextEditorOpen.value || !selectedTextElement.value) {
            return;
        }

        const container = document.createElement('div');
        container.innerHTML = props.html || '';

        if (!findNodeByPath(container, selectedTextElement.value.path)) {
            closeTextEditor();
        }
    }
);

watch(
    () => props.interactionLocked,
    (isLocked) => {
        if (isLocked) {
            closeTextEditor();
        }
    }
);

watch(
    [isTextEditorOpen, editorDraftHtml],
    async ([open]) => {
        if (!open) {
            return;
        }

        await nextTick();
        syncEditorContent();
    },
    { flush: 'post' }
);

onMounted(() => {
    window.addEventListener('message', handleMessage);
    syncInheritedHeadHtml();

    headObserver = new MutationObserver(() => {
        syncInheritedHeadHtml();
    });
    headObserver.observe(document.head, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['href', 'media'],
    });

    ensureSnapshotRoot();
    scheduleRenderedHtmlUpdate();
});

onUnmounted(() => {
    window.removeEventListener('message', handleMessage);
    headObserver?.disconnect();
    headObserver = null;
    window.clearTimeout(scheduledRenderTimeout);
    scheduledRenderTimeout = null;
    window.clearTimeout(textEditorFocusTimeout);
    textEditorFocusTimeout = null;

    if (snapshotRoot.value) {
        snapshotRoot.value.remove();
        snapshotRoot.value = null;
    }
});

defineExpose({
    refreshIframe,
    isIframeRefreshing,
});
</script>

<template>
    <div class="relative h-full w-full">
        <iframe
            v-if="isIframeMounted"
            :key="iframeRenderKey"
            ref="iframeRef"
            :srcdoc="wrappedHtml"
            class="w-full h-full"
            :class="props.viewmode !== 'app-native' ? 'border rounded-lg shadow-sm bg-white' : ''"
            sandbox="allow-scripts"
            @load="restoreScroll"
        ></iframe>

        <div
            v-else
            class="h-full w-full rounded-lg border bg-white shadow-sm"
        ></div>

        <slideout-with-slots
            v-if="isTextEditorOpen"
            :title="_mft('page:aiPageBuilder.preview.editText')"
            :header-style="props.viewmode === 'app-native' ? 'minimal' : 'default'"
            max-width-class="max-w-[400px]"
            @close="closeTextEditor"
        >
            <template #actions>
                <div class="mb-2 flex items-center justify-end gap-3">
                    <button-secondary
                        v-if="props.viewmode !== 'app-native'"
                        @click="closeTextEditor"
                    >
                        {{ _mft('shared:action.cancel') }}
                    </button-secondary>
                    <button-success
                        v-if="props.viewmode !== 'app-native'"
                        @click="saveTextEdit"
                    >
                        {{ _mft('shared:action.saveChanges') }}
                    </button-success>

                    <button
                        v-if="props.viewmode === 'app-native'"
                        @click="closeTextEditor"
                        class="text-xs mr-4 font-semibold"
                    >{{ _mft('shared:action.cancel') }}</button>
                    <button
                        v-if="props.viewmode === 'app-native'"
                        @click="saveTextEdit"
                        class="aspect-square rounded-full flex justify-center items-center bg-myflowPurple text-white w-9 h-9 -mr-1"
                    ><i class="fa-regular fa-check"></i></button>
                </div>
            </template>

            <template #body>
                <div>
                    <div class="hidden">
                        <div class="text-xs font-medium uppercase tracking-[0.12em] text-gray-400">
                            {{ _mft('page:aiPageBuilder.preview.selectedElement') }}
                        </div>
                        <div class="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-gray-600">
                            {{ selectedTextElement?.tag || _mft('page:aiPageBuilder.preview.textElementFallback') }}
                        </div>
                    </div>

                    <div class="flex items-center gap-2 bg-gray-100 p-1 rounded-t-lg border-l border-t border-r pb-3">
                        <button
                            type="button"
                            class="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-300 bg-white text-sm font-bold text-gray-700 transition hover:bg-gray-50"
                            :title="_mft('page:aiPageBuilder.preview.bold')"
                            @click="applyEditorFormat('bold')"
                        >
                            B
                        </button>
                        <button
                            type="button"
                            class="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-300 bg-white text-sm italic text-gray-700 transition hover:bg-gray-50"
                            :title="_mft('page:aiPageBuilder.preview.italic')"
                            @click="applyEditorFormat('italic')"
                        >
                            I
                        </button>
                        <button
                            type="button"
                            class="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-300 bg-white text-sm underline text-gray-700 transition hover:bg-gray-50"
                            :title="_mft('page:aiPageBuilder.preview.underline')"
                            @click="applyEditorFormat('underline')"
                        >
                            U
                        </button>
                    </div>

                    <div
                        ref="editorContentRef"
                        contenteditable="true"
                        id="element-text-editor"
                        class="-mt-2 min-h-[280px] w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm leading-6 !text-gray-800 shadow-sm outline-none transition focus:border-aiAgentWebDesigner focus:ring-2 focus:ring-aiAgentWebDesigner/40"
                        @input="handleEditorInput"
                        @keydown="handleEditorKeydown"
                    ></div>
                </div>

            </template>
        </slideout-with-slots>
    </div>
</template>
<style>
#element-text-editor {
    color: #3f3f3f !important;
}
#element-text-editor .text-transparent {
    color: #3f3f3f !important;
}
</style>
